import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sunrise, X, ArrowRight, Sparkles, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MorningMessage({ open = false, onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showBrief, setShowBrief] = useState(false);

  const { data: msg } = useQuery({
    queryKey: ["morning-message"],
    queryFn: async () => (await api.get("/recap/morning-message")).data,
  });

  const dismissMut = useMutation({
    mutationFn: () => api.post("/recap/morning-message/dismiss"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["morning-message"] });
      if (onClose) onClose();
    },
  });

  // Only render when the parent toggle is open AND there's actual content to show.
  if (!open) return null;
  if (!msg || (!msg.message_to_future_self && !msg.edge_brief)) return null;

  return (
    <div className="relative mx-8 mt-6 border border-emerald-400/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent" data-testid="morning-banner">
      <div className="p-6 flex items-start gap-5">
        <div className="shrink-0 w-10 h-10 bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
          <Sunrise className="h-5 w-5 text-emerald-400" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <div className="label-eyebrow text-emerald-400 flex items-center gap-2">
              GOOD MORNING · FROM {msg.date}
            </div>
          </div>

          {msg.message_to_future_self ? (
            // Your note — large, prominent, exactly what you wrote.
            <div className="border-l-2 border-emerald-400/40 pl-4">
              <div className="label-eyebrow mb-1.5 text-zinc-500">A note you left yourself</div>
              <p className="text-lg text-zinc-100 font-mono-num leading-relaxed whitespace-pre-wrap" data-testid="morning-message-text">
                {msg.message_to_future_self}
              </p>
            </div>
          ) : (
            // No personal note — fall back to showing the brief so the banner isn't empty.
            msg.edge_brief && (
              <div className="border-l-2 border-emerald-400/40 pl-4">
                <div className="label-eyebrow mb-1.5 text-emerald-400 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" strokeWidth={1.5} /> COACH BRIEF
                </div>
                <p className="text-sm text-zinc-300 font-mono-num leading-relaxed whitespace-pre-wrap" data-testid="morning-brief-text">
                  {msg.edge_brief}
                </p>
              </div>
            )
          )}

          {/* Collapsible Coach Ross brief — only when there's also a personal note */}
          {msg.message_to_future_self && msg.edge_brief && (
            <div className="border-t border-emerald-400/20 pt-3">
              <button
                onClick={() => setShowBrief((s) => !s)}
                data-testid="toggle-brief-btn"
                className="flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-emerald-400 transition-colors font-mono-num"
              >
                <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                {showBrief ? "HIDE" : "SHOW"} COACH BRIEF
                <ChevronDown className={`h-3 w-3 transition-transform ${showBrief ? "rotate-180" : ""}`} strokeWidth={1.5} />
              </button>
              {showBrief && (
                <div className="border-l-2 border-emerald-400/30 pl-4 mt-3" data-testid="brief-collapsible">
                  <p className="text-sm text-zinc-300 font-mono-num leading-relaxed whitespace-pre-wrap" data-testid="morning-brief-text">
                    {msg.edge_brief}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 pt-1">
            <button onClick={() => { dismissMut.mutate(); navigate(`/recap/${msg.date}`); }}
              data-testid="open-recap-btn"
              className="flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
              OPEN THAT DAY&apos;S RECAP <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
            </button>
            <button onClick={() => { dismissMut.mutate(); onClose && onClose(); }}
              data-testid="dismiss-morning-btn"
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
              DISMISS
            </button>
            <button onClick={onClose}
              data-testid="close-morning-btn"
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors ml-auto">
              CLOSE
            </button>
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-zinc-300 transition-colors">
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
